import React, { useMemo } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { DataTable } from "@/components/ui/table";
import Pagination from "@/pages/PortPlace/Pagination";
import { buildReportColumns } from "@/pages/reports/reportTableHelper";
import { useClientReportPagination } from "@/pages/reports/shared/useClientReportPagination";
import { useReportGridPipeline } from "@/pages/reports/shared/useReportGridPipeline";
import { useReportGridPreferences } from "@/pages/reports/shared/useReportGridPreferences";
import ReportGridToolbar from "./ReportGridToolbar";
import {
    reportCard,
    reportHeading,
    reportMutedText,
    reportPaginationWrap,
    reportSectionTitle,
    reportSortBtn,
    reportTableHeader,
    reportTableRow,
    reportTableTd,
    reportTableTh,
    reportTableThCompact,
    reportTableTdCompact,
    reportTableRowCompact,
} from "./reportTheme";

const ReportDataGrid = ({
    gridId,
    data = [],
    columns: columnsProp,
    columnOptions,
    emptyText = "No report data found for the selected filters.",
    title,
    defaultPageSize = 10,
    paginationTitle = "records",
    enableToolbar = true,
    serverPagination = null,
    bare = false,
    compact = false,
}) => {
    const isServer = Boolean(serverPagination);

    const allColumns = useMemo(() => {
        const built = columnsProp ?? buildReportColumns(data, columnOptions);
        return built.map((col) => ({ sortable: col.sortable !== false, ...col }));
    }, [columnsProp, data, columnOptions]);

    const allColumnKeys = useMemo(() => allColumns.map((c) => c.key), [allColumns]);

    const {
        visibleKeys,
        pageSize,
        setPageSize,
        toggleColumn,
        showAllColumns,
        resetPreferences,
    } = useReportGridPreferences(gridId, allColumnKeys, defaultPageSize);

    const visibleColumns = useMemo(
        () => allColumns.filter((c) => visibleKeys.includes(c.key)),
        [allColumns, visibleKeys]
    );

    const {
        searchText,
        setSearchText,
        clearSearch,
        sortKey,
        sortDir,
        toggleSort,
        processedData,
    } = useReportGridPipeline(data, allColumns);

    const clientPageSize = isServer ? serverPagination.pageSize : pageSize;

    const {
        currentPage,
        recordsPerPage,
        setRecordsPerPage,
        paginatedItems,
        totalRecord: clientTotal,
        handlePrevious,
        handleNext,
        handleGoto,
    } = useClientReportPagination(processedData, clientPageSize);

    const tableData = isServer ? (searchText.trim() ? processedData : data) : paginatedItems;
    const displayTotal = isServer
        ? serverPagination.totalRecords ?? data?.length ?? 0
        : Math.max(clientTotal, data?.length ?? 0);
    const activePage = isServer ? serverPagination.page : currentPage;

    const handlePageSizeChange = (size) => {
        if (isServer) serverPagination.onPageSizeChange?.(size);
        else {
            setRecordsPerPage(size);
            setPageSize(size);
        }
    };

    const sortableColumns = visibleColumns.map((col) => {
        if (col.sortable === false || isServer) return col;
        const labelText = typeof col.label === "string" ? col.label : col.key;
        return {
            ...col,
            label: () => (
                <button type="button" onClick={() => toggleSort(col.key)} className={reportSortBtn}>
                    {labelText}
                    {sortKey === col.key ? (
                        sortDir === "asc" ? (
                            <ArrowUp className="h-3.5 w-3.5" />
                        ) : (
                            <ArrowDown className="h-3.5 w-3.5" />
                        )
                    ) : (
                        <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />
                    )}
                </button>
            ),
        };
    });

    const wrapperClass = bare
        ? "overflow-hidden bg-transparent"
        : `${reportCard} overflow-hidden transition-all duration-200`;

    const thClassUsed = compact ? reportTableThCompact : reportTableTh;
    const tdClassUsed = compact ? reportTableTdCompact : reportTableTd;
    const trClassUsed = compact ? reportTableRowCompact : reportTableRow;

    return (
        <div className="flex flex-col gap-0">
            {title && !bare && (
                <div className={reportSectionTitle}>
                    <span className={`text-sm font-semibold ${reportHeading}`}>{title}</span>
                    {!isServer && searchText.trim() && processedData.length !== data.length && (
                        <span className={`text-xs ml-2 ${reportMutedText}`}>
                            ({processedData.length} of {data.length} filtered)
                        </span>
                    )}
                </div>
            )}

            <div className={wrapperClass}>
                {enableToolbar && gridId && allColumns.length > 0 && (
                    <>
                        <ReportGridToolbar
                            searchText={searchText}
                            onSearchChange={setSearchText}
                            onClearSearch={clearSearch}
                            columns={allColumns}
                            visibleKeys={visibleKeys}
                            onToggleColumn={toggleColumn}
                            onShowAllColumns={showAllColumns}
                            onResetPreferences={resetPreferences}
                        />
                        {isServer && (
                            <p
                                className={`border-b border-gray-100 px-4 sm:px-5 py-2 text-xs dark:border-gray-800 ${reportMutedText}`}
                            >
                                Server paging — use filters or pager below to load pages.
                            </p>
                        )}
                    </>
                )}

                <DataTable
                    data={tableData}
                    columns={sortableColumns}
                    trClass={trClassUsed}
                    thClass={thClassUsed}
                    tdClass={tdClassUsed}
                    htrClass={reportTableHeader}
                    emptyText={emptyText}
                    boxClass="border-0 rounded-none shadow-none bg-transparent dark:bg-transparent overflow-hidden"
                />
            </div>

            {(displayTotal > 0 || isServer) && (
                <Pagination
                    currentPage={activePage}
                    recordsPerPage={isServer ? serverPagination.pageSize : recordsPerPage}
                    setRecordsPerPage={handlePageSizeChange}
                    onPrevious={(p) => (isServer ? serverPagination.onPageChange?.(p) : handlePrevious(p))}
                    onNext={(p) => (isServer ? serverPagination.onPageChange?.(p) : handleNext(p))}
                    totalRecord={displayTotal}
                    handleGoto={(p) => (isServer ? serverPagination.onPageChange?.(p) : handleGoto(p))}
                    title={paginationTitle}
                    className={reportPaginationWrap}
                />
            )}
        </div>
    );
};

export default ReportDataGrid;
